'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

// Tipos iguais ao Google Meet
export type LayoutMode = 'auto' | 'grid' | 'spotlight' | 'sidebar';

interface LayoutViewContextType {
  layoutMode: LayoutMode;
  setLayoutMode: (mode: LayoutMode) => void;
  maxTiles: number;
  setMaxTiles: (n: number) => void;
  setMaxTilesFromUser: (n: number) => void;
  maxTilesUserOverride: boolean;
  hideNonVideo: boolean;
  setHideNonVideo: (b: boolean) => void;
  isLayoutModalOpen: boolean;
  openLayoutModal: () => void;
  closeLayoutModal: () => void;
  toggleLayoutModal: () => void;
}

export const LayoutViewContext = createContext<LayoutViewContextType | undefined>(undefined);

export function LayoutViewProvider({ children }: { children: React.ReactNode }) {
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('auto');
  const [maxTiles, setMaxTilesState] = useState<number>(9);
  const [maxTilesUserOverride, setMaxTilesUserOverride] = useState(false);
  const [hideNonVideo, setHideNonVideo] = useState<boolean>(false);
  const [isLayoutModalOpen, setIsLayoutModalOpen] = useState(false);

  const setMaxTiles = useCallback((n: number) => {
    setMaxTilesState(n);
  }, []);

  const setMaxTilesFromUser = useCallback((n: number) => {
    setMaxTilesUserOverride(true);
    setMaxTilesState(n);
  }, []);

  const openLayoutModal = useCallback(() => {
    setIsLayoutModalOpen(true);
  }, []);

  const closeLayoutModal = useCallback(() => {
    setIsLayoutModalOpen(false);
  }, []);

  const toggleLayoutModal = useCallback(() => {
    setIsLayoutModalOpen((open) => !open);
  }, []);

  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('em-meet-modal-open', isLayoutModalOpen);
    return () => {
      document.body.classList.remove('em-meet-modal-open');
    };
  }, [isLayoutModalOpen]);

  const value = useMemo(
    () => ({
      layoutMode,
      setLayoutMode,
      maxTiles,
      setMaxTiles,
      setMaxTilesFromUser,
      maxTilesUserOverride,
      hideNonVideo,
      setHideNonVideo,
      isLayoutModalOpen,
      openLayoutModal,
      closeLayoutModal,
      toggleLayoutModal,
    }),
    [
      layoutMode,
      maxTiles,
      maxTilesUserOverride,
      hideNonVideo,
      isLayoutModalOpen,
      openLayoutModal,
      closeLayoutModal,
      toggleLayoutModal,
    ],
  );

  return <LayoutViewContext.Provider value={value}>{children}</LayoutViewContext.Provider>;
}

export const useLayoutView = () => {
  const context = useContext(LayoutViewContext);
  if (!context) throw new Error('useLayoutView must be used within a LayoutViewProvider');
  return context;
};